// src/composables/useNavigation.ts
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { NavigationItem } from "@/components/Menu/type";

export interface FooterLink {
  text: string;
  href: string;
  external?: boolean;
}

export interface FooterSection {
  title: string;
  links: FooterLink[];
  flexClass?: string;
}

export interface FooterContactInfo {
  title: string;
  lines: string[];
}

export function useNavigation() {
  const { t } = useI18n();

  // ========== 头部导航 ==========
  const mainMenuItems = computed<NavigationItem[]>(() => [
    { id: "1", label: t("nav.home"), href: "/" },
    { id: "2", label: t("nav.artworks"), href: "/works/painting" },
    { id: "3", label: t("nav.associationMembers"), href: "/artist" },
    {
      id: "4",
      label: t("nav.about"),
      children: [
        { id: "5-1", label: t("nav.associationIntro"), href: "/about" },
        { id: "5-2", label: t("nav.charter"), href: "/about/constitution" },
      ],
    },
    { id: "5", label: t("nav.memberQuery"), href: "/member-query" },
    { id: "6", label: t("nav.blog"), href: "/blog" },
    {
      id: "7",
      label: t("nav.joinUs"),
      children: [
        { id: "8-1", label: t("nav.applyMembership"), href: "/contact" },
        { id: "8-2", label: t("nav.feeQuery"), href: "/FeeQuery" },
      ],
    },
    { id: "8", label: t("nav.contactUs"), href: "/" },
  ]);

  // ========== PC 端 Footer 导航 ==========
  const footerNavigationSections = computed<FooterSection[]>(() => [
    {
      title: t("footer.aboutUs"),
      flexClass: "flex-1",
      links: [
        { text: t("footer.associationIntro"), href: "/about/introduction" },
        {
          text: t("footer.associationConstitution"),
          href: "/about/constitution",
        },
        {
          text: t("footer.exhibitionShipping"),
          href: "/about/exhibition-shipping",
        },
        { text: t("footer.memberRules"), href: "/about/memberRules" },
        { text: t("footer.associationHonors"), href: "/about/honors" },
        { text: t("footer.joinApplication"), href: "/membership" },
      ],
    },
    {
      title: t("footer.excellentMembers"),
      flexClass: "flex-1",
      links: [
        { text: t("footer.visualArtists"), href: "/members/visual-artists" },
        {
          text: t("footer.performingArtists"),
          href: "/members/performing-artists",
        },
        {
          text: t("footer.literaryArtists"),
          href: "/members/literary-artists",
        },
        { text: t("footer.filmArtists"), href: "/members/film-artists" },
      ],
    },
    {
      title: t("footer.infoDisclosure"),
      flexClass: "flex-1",
      links: [
        {
          text: t("footer.associationNews"),
          href: "/news/association",
          external: true,
        },
        {
          text: t("footer.industryNews"),
          href: "/news/industry",
          external: true,
        },
      ],
    },
    {
      title: t("footer.artworks"),
      flexClass: "flex-1",
      links: [
        { text: t("footer.chinesePainting"), href: "/works/painting" },
        { text: t("footer.calligraphy"), href: "/works/painting" },
        { text: t("footer.crafts"), href: "/works/painting" },
      ],
    },
  ]);

  const footerContactInfo = computed(() => ({
    title: t("footer.contactUs"),
    lines: [
      t("footer.usaHeadquarters"),
      t("footer.usaPhone"),
      "Email: marketing@artgiving.org",
      t("footer.chinaOffice"),
      t("footer.chinaPhone"),
      "Email: evadjie@artgiving.org",
    ],
  }));

  // ========== 移动端 Footer 导航（手风琴样式） ==========
  const mobileFooterMenuItems = computed<NavigationItem[]>(() => [
    {
      id: "1",
      label: t("footer.aboutUs"),
      textClass: "text-black",
      children: [
        {
          id: "1-1",
          label: t("footer.associationIntro"),
          href: "/about/introduction",
        },
        {
          id: "1-2",
          label: t("footer.associationConstitution"),
          href: "/about/constitution",
        },
        {
          id: "1-3",
          label: t("footer.exhibitionShipping"),
          href: "/about/exhibition-shipping",
        },
        {
          id: "1-4",
          label: t("footer.memberRules"),
          href: "/about/memberRules",
        },
        {
          id: "1-5",
          label: t("footer.associationHonors"),
          href: "/about/honors",
        },
        {
          id: "1-6",
          label: t("footer.joinApplication"),
          href: "/membership",
          textClass: "text-black",
        },
      ],
    },
    {
      id: "2",
      label: t("footer.excellentMembers"),
      textClass: "text-black",
      children: [
        { id: "2-1", label: t("footer.visualArtists"), href: "/" },
        { id: "2-2", label: t("footer.performingArtists"), href: "/" },
        { id: "2-3", label: t("footer.literaryArtists"), href: "/" },
        { id: "2-4", label: t("footer.filmArtists"), href: "/" },
      ],
    },
    {
      id: "3",
      label: t("footer.associationNews"),
      textClass: "text-black",
      children: [
        { id: "3-1", label: t("footer.associationNews"), href: "/" },
        { id: "3-2", label: t("footer.industryNews"), href: "/" },
      ],
    },
    {
      id: "4",
      label: t("footer.artworks"),
      textClass: "text-black",
      children: [
        {
          id: "4-1",
          label: t("footer.chinesePainting"),
          href: "/works/painting",
        },
        {
          id: "4-2",
          label: t("footer.calligraphy"),
          href: "/works/painting",
        },
        {
          id: "4-3",
          label: t("footer.crafts"),
          href: "/works/painting",
        },
      ],
    },
    {
      id: "5",
      label: t("footer.contactUs"),
      textClass: "text-black",
      children: [
        { id: "5-1", label: t("footer.usaHeadquarters") },
        { id: "5-2", label: t("footer.usaPhone") },
        { id: "5-3", label: t("footer.chinaOffice") },
        { id: "5-4", label: t("footer.chinaPhone") },
      ],
    },
  ]);

  return {
    // 头部导航
    mainMenuItems,

    // PC 端 Footer
    footerNavigationSections,
    footerContactInfo,

    // 移动端 Footer
    mobileFooterMenuItems,
  };
}
